import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { fetchDesenvolvimento, addVinculo, removeVinculo, fetchArtigosKB } from '../../../api/desenvolvimento';
import { fetchRepositorios, fetchArquivos } from '../../../api/repositorios';
import './DesenvolvimentoCard.css';

const STATUS_SPRINT_LABEL = { backlog: 'Backlog', fazendo: 'Fazendo', revisao: 'Revisão', concluido: 'Concluído' };

function formatarDataHora(d) {
    if (!d) return '';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function DesenvolvimentoCard({ satNum, embedded = false }) {
    const [dados, setDados] = useState(null);
    const [loading, setLoading] = useState(true);
    const [mensagem, setMensagem] = useState('');

    // modal de vínculo
    const [showModal, setShowModal] = useState(false);
    const [tipo, setTipo] = useState('repo'); // repo | kb
    const [repos, setRepos] = useState([]);
    const [repoId, setRepoId] = useState('');
    const [arquivos, setArquivos] = useState([]);
    const [catFiltro, setCatFiltro] = useState(''); // '' = todas
    const [arquivoId, setArquivoId] = useState('');
    const [artigos, setArtigos] = useState([]);
    const [artigoId, setArtigoId] = useState('');
    const [salvando, setSalvando] = useState(false);

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const d = await fetchDesenvolvimento(satNum);
            setDados(d || { sprint_itens: [], repo_commits: [], repo_arquivos: [], kb_artigos: [] });
        } finally {
            setLoading(false);
        }
    }, [satNum]);

    useEffect(() => { if (satNum) carregar(); }, [satNum, carregar]);

    const abrirModal = async () => {
        setMensagem('');
        setTipo('repo');
        setRepoId(''); setArquivoId(''); setArquivos([]); setArtigoId(''); setCatFiltro('');
        setShowModal(true);
        const [rs, as] = await Promise.all([fetchRepositorios(), fetchArtigosKB()]);
        setRepos(Array.isArray(rs) ? rs : []);
        setArtigos(Array.isArray(as) ? as : []);
    };

    useEffect(() => {
        if (!repoId) { setArquivos([]); setArquivoId(''); setCatFiltro(''); return; }
        fetchArquivos(repoId).then((l) => setArquivos(Array.isArray(l) ? l : []));
    }, [repoId]);

    // Categorias presentes nos arquivos do repositório selecionado.
    const categoriasDoRepo = (() => {
        const map = new Map();
        arquivos.forEach((a) => {
            const k = a.categoria_id == null ? 'sem' : String(a.categoria_id);
            if (!map.has(k)) map.set(k, { key: k, nome: a.categoria_nome || 'Sem categoria' });
        });
        return Array.from(map.values());
    })();

    const arquivosFiltrados = arquivos.filter((a) => {
        if (!catFiltro) return true;
        const k = a.categoria_id == null ? 'sem' : String(a.categoria_id);
        return k === catFiltro;
    });

    const vincular = async () => {
        let destino_tipo, destino_id;
        if (tipo === 'repo') {
            if (!arquivoId) { setMensagem('Selecione o arquivo.'); return; }
            destino_tipo = 'repo_arquivo'; destino_id = arquivoId;
        } else {
            if (!artigoId) { setMensagem('Selecione o artigo.'); return; }
            destino_tipo = 'kb_artigo'; destino_id = artigoId;
        }
        setSalvando(true);
        setMensagem('');
        try {
            await addVinculo({ origem_tipo: 'chamado', origem_id: satNum, destino_tipo, destino_id });
            setShowModal(false);
            await carregar();
        } catch (e) {
            setMensagem(e?.response?.data?.error || 'Erro ao vincular.');
        } finally {
            setSalvando(false);
        }
    };

    const desvincular = async (linkId) => {
        try {
            await removeVinculo(linkId);
            await carregar();
        } catch (e) {
            setMensagem(e?.response?.data?.error || 'Erro ao remover vínculo.');
        }
    };

    const d = dados || { sprint_itens: [], repo_commits: [], repo_arquivos: [], kb_artigos: [] };
    const vazio = !loading && d.sprint_itens.length === 0 && d.repo_commits.length === 0 && d.repo_arquivos.length === 0 && d.kb_artigos.length === 0;

    const wrapClass = embedded ? 'dev-embedded' : 'container-fluid px-3 dev-card-wrap';
    const cardClass = embedded ? 'dev-card dev-card--embedded' : 'card dev-card';

    return (
        <div className={wrapClass}>
            <div className={cardClass}>
                {!embedded && (
                    <div className="card-header dev-card-header d-flex justify-content-between align-items-center">
                        <span><i className="fas fa-code-branch me-2" />Desenvolvimento</span>
                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={abrirModal}>
                            <i className="fas fa-link me-1" />Vincular
                        </button>
                    </div>
                )}
                <div className={embedded ? 'dev-embedded-body' : 'card-body'}>
                    {embedded && (
                        <div className="dev-embedded-toolbar d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                            <span className="dev-embedded-titulo"><i className="fas fa-code-branch me-2" />Desenvolvimento</span>
                            <button type="button" className="btn btn-sm btn-outline-primary" onClick={abrirModal}>
                                <i className="fas fa-link me-1" />Vincular
                            </button>
                        </div>
                    )}
                    {loading && <p className="text-muted small mb-0">Carregando...</p>}
                    {mensagem && <div className="alert alert-danger py-2 mb-2">{mensagem}</div>}

                    {vazio && (
                        <p className="text-muted small mb-0">
                            Nada vinculado ainda. Commits do repositório com este chamado e itens de sprint aparecem aqui automaticamente; use “Vincular” para anexar arquivos ou artigos.
                        </p>
                    )}

                    {!loading && !vazio && (
                        <div className="dev-grid">
                            {/* Itens de sprint */}
                            <div className="dev-bloco">
                                <div className="dev-bloco-titulo"><i className="fas fa-tasks me-1" />Sprint ({d.sprint_itens.length})</div>
                                {d.sprint_itens.length === 0 && <div className="dev-vazio">—</div>}
                                {d.sprint_itens.map((s) => (
                                    <Link key={s.sprint_chamado_id} to={`/sprint/${s.sprint_id}`} className="dev-item">
                                        <span className="dev-item-nome">{s.sprint_nome}</span>
                                        <span className={`badge ${s.status === 'concluido' ? 'bg-success' : 'bg-secondary'}`}>{STATUS_SPRINT_LABEL[s.status] || s.status}</span>
                                    </Link>
                                ))}
                            </div>

                            {/* Arquivos e commits do repositório */}
                            <div className="dev-bloco">
                                <div className="dev-bloco-titulo"><i className="fas fa-folder me-1" />Repositório ({d.repo_arquivos.length})</div>
                                {d.repo_arquivos.length === 0 && d.repo_commits.length === 0 && <div className="dev-vazio">—</div>}
                                {d.repo_arquivos.map((a) => (
                                    <div key={a.arquivo_id} className="dev-item">
                                        <Link to={`/repositorios/${a.repositorio_id}?arquivo=${a.arquivo_id}`} className="dev-item-nome" title={`${a.repositorio_nome} · v${a.versao_atual}`}>
                                            <i className="fas fa-file me-1" />{a.caminho}
                                        </Link>
                                        {a.fonte === 'commit' ? <span className="badge bg-light text-dark" title="Via commit">commit</span> : null}
                                        {a.link_id ? (
                                            <button type="button" className="dev-x" title="Remover vínculo" onClick={() => desvincular(a.link_id)}><i className="fas fa-times" /></button>
                                        ) : null}
                                    </div>
                                ))}
                                {d.repo_commits.map((c) => (
                                    <div key={`c${c.commit_id}`} className="dev-item dev-commit" title={formatarDataHora(c.criado_em)}>
                                        <i className="fas fa-code-commit me-1" />
                                        <span className="dev-item-nome">{c.mensagem || '(sem mensagem)'}</span>
                                        <span className="dev-commit-meta">{c.autor_nome || '—'}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Base de conhecimento */}
                            <div className="dev-bloco">
                                <div className="dev-bloco-titulo"><i className="fas fa-book me-1" />Base ({d.kb_artigos.length})</div>
                                {d.kb_artigos.length === 0 && <div className="dev-vazio">—</div>}
                                {d.kb_artigos.map((k) => (
                                    <div key={k.id} className="dev-item">
                                        <span className="dev-item-nome"><i className="fas fa-file-lines me-1" />{k.titulo}</span>
                                        {k.link_id ? (
                                            <button type="button" className="dev-x" title="Remover vínculo" onClick={() => desvincular(k.link_id)}><i className="fas fa-times" /></button>
                                        ) : null}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {showModal && (
                <div className="modal-overlay" onClick={() => !salvando && setShowModal(false)}>
                    <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                        <h5 className="mb-3"><i className="fas fa-link me-2" />Vincular ao chamado #{satNum}</h5>
                        <div className="form-group mb-2">
                            <label>O que vincular</label>
                            <select className="form-control form-control-sm" value={tipo} onChange={(e) => setTipo(e.target.value)}>
                                <option value="repo">Arquivo do repositório</option>
                                <option value="kb">Artigo da base de conhecimento</option>
                            </select>
                        </div>

                        {tipo === 'repo' && (
                            <>
                                <div className="form-group mb-2">
                                    <label>Repositório</label>
                                    <select className="form-control form-control-sm" value={repoId} onChange={(e) => setRepoId(e.target.value)}>
                                        <option value="">Selecione...</option>
                                        {repos.map((r) => <option key={r.id} value={r.id}>{r.nome}</option>)}
                                    </select>
                                </div>
                                <div className="form-group mb-2">
                                    <label>Categoria</label>
                                    <select className="form-control form-control-sm" value={catFiltro}
                                        onChange={(e) => { setCatFiltro(e.target.value); setArquivoId(''); }} disabled={!repoId}>
                                        <option value="">{repoId ? 'Todas' : 'Escolha o repositório primeiro'}</option>
                                        {categoriasDoRepo.map((c) => <option key={c.key} value={c.key}>{c.nome}</option>)}
                                    </select>
                                </div>
                                <div className="form-group mb-2">
                                    <label>Arquivo</label>
                                    <select className="form-control form-control-sm" value={arquivoId} onChange={(e) => setArquivoId(e.target.value)} disabled={!repoId}>
                                        <option value="">{repoId ? 'Selecione...' : 'Escolha o repositório primeiro'}</option>
                                        {arquivosFiltrados.map((a) => (
                                            <option key={a.id} value={a.id}>
                                                {a.caminho}{a.categoria_nome ? ` — ${a.categoria_nome}` : ''}{a.projeto ? ` / ${a.projeto}` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </>
                        )}

                        {tipo === 'kb' && (
                            <div className="form-group mb-2">
                                <label>Artigo</label>
                                <select className="form-control form-control-sm" value={artigoId} onChange={(e) => setArtigoId(e.target.value)}>
                                    <option value="">Selecione...</option>
                                    {artigos.map((a) => <option key={a.id} value={a.id}>{a.titulo}</option>)}
                                </select>
                            </div>
                        )}

                        {mensagem && <div className="alert alert-danger py-2 mb-2">{mensagem}</div>}
                        <div className="d-flex justify-content-end gap-2">
                            <button type="button" className="btn btn-secondary btn-sm" disabled={salvando} onClick={() => setShowModal(false)}>Cancelar</button>
                            <button type="button" className="btn btn-primary btn-sm" disabled={salvando} onClick={vincular}>{salvando ? 'Vinculando...' : 'Vincular'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DesenvolvimentoCard;
